import { addSuppressedError } from '@shared/utils/logger';

/**
 * Joy photos live behind two independent image services with different query dialects:
 *
 * | backend         | path prefix        | width param | placeholder      |
 * | --------------- | ------------------ | ----------- | ---------------- |
 * | media_service   | `/media/`          | `width=N`   | `rendition=tiny` |
 * | asset-optimizer | `/assets/public/`  | `resize=wN` | `resize=w48`     |
 *
 * asset-optimizer (repo: joylifeinc/asset-optimizer, Lambda behind CloudFront) ignores unknown
 * params and serves an auto-capped default — which is exactly the bug this module fixes: sending
 * it media_service vocabulary (`rendition=`/`width=`) silently returns the same file for every
 * srcset candidate. See doc/improve-guest-site-perf/18-guest-lcp-p80-under-2s/README.md.
 *
 * `w48` matches media_service `tiny`'s ~48px short edge (measured 514 B on the Brannan default).
 */
/**
 * Whether a URL can carry image-service params at all.
 *
 * Deliberately duplicated from photoRendition.ts's identical private predicate rather than shared
 * via an import. Importing it would make this module depend on photoRendition at runtime, and
 * seven specs mock that module with a partial factory (`{ addRendition }` only) — so the shared
 * symbol resolves to `undefined` and every consumer of this file throws inside those specs. Same
 * hazard applies to urlHelpers' equivalent `isUrlWithHttpProtocol`, which is itself automocked in
 * one spec and would additionally drag `query-string` onto the guest SSR/preload path.
 *
 * The two copies are each pinned by their own passthrough specs, so drift fails a test rather than
 * shipping. Keep them in sync: only absolute http(s) URLs reach an image service.
 */
const isHttpUrl = (url: string): boolean => /^https?:\/\//i.test(url);

const MEDIA_SERVICE_PREFIX = '/media/';
const ASSET_OPTIMIZER_PREFIX = '/assets/public/';
const ASSET_PLACEHOLDER_WIDTH = 48;

export type PhotoUrlRequest = Readonly<{ width: number }> | Readonly<{ placeholder: true }>;

/**
 * Rewrites a photo URL to request a specific width (or the blur placeholder) in the dialect of
 * whichever backend serves it. URLs on neither backend — including non-`/assets/public/` paths
 * under `/assets/` — pass through unchanged, as do unparseable strings.
 *
 * Width requests strip `rendition` (media_service resolves rendition BEFORE width, so a URL
 * carrying both silently ignores the width — see buildHighResSrcSet's doc in renditions.ts).
 */
export const buildPhotoUrl = (url: string, request: PhotoUrlRequest): string => {
  // Gate before parsing: a relative/protocol-relative path (a bundled default asset, say) would
  // throw and report a suppressed error on EVERY call, and a `data:` URI parses, so params would
  // land on its base64 payload. Neither is an image-service URL.
  if (typeof url !== 'string' || !isHttpUrl(url)) return url;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch (err) {
    // Still guarded: an http(s)-prefixed but malformed URL (e.g. a bad host) reaches here.
    addSuppressedError(err, { context: 'buildPhotoUrl', url });
    return url;
  }

  const isMedia = parsed.pathname.startsWith(MEDIA_SERVICE_PREFIX);
  const isAssetOptimizer = parsed.pathname.startsWith(ASSET_OPTIMIZER_PREFIX);
  if (!isMedia && !isAssetOptimizer) return url;

  if (isMedia) {
    if ('width' in request) {
      parsed.searchParams.delete('rendition');
      parsed.searchParams.set('width', String(request.width));
    } else {
      parsed.searchParams.set('rendition', 'tiny');
    }
    return parsed.toString();
  }

  // asset-optimizer: rendition/width are dead weight it ignores — remove so cache keys stay clean.
  parsed.searchParams.delete('rendition');
  parsed.searchParams.delete('width');
  const targetWidth = 'width' in request ? request.width : ASSET_PLACEHOLDER_WIDTH;
  parsed.searchParams.set('resize', `w${targetWidth}`);
  return parsed.toString();
};
